import { LOCALES, type Locale } from './locales.js';

/**
 * Every article's URL segment, in every language.
 *
 * Small enough to ship in the main bundle, and it has to be: the language menu on an
 * article page needs French's slug while the browser has only loaded English. Deriving it
 * from the article bodies would give the server four options and the client one, which
 * React resolves by throwing the prerendered page away.
 *
 * This map is also the authority on which translations exist. An id with no entry for a
 * language simply has no article there, and `tests/unit/articles.test.ts` asserts the map
 * and the article files agree in both directions, so neither can drift.
 */
export const ARTICLE_SLUGS: Record<string, Partial<Record<Locale, string>>> = {
  'bachelorette-cost-per-person': {
    en: 'how-much-should-a-bachelorette-party-cost-per-person',
    fr: 'combien-coute-un-evjf-par-personne',
    es: 'cuanto-cuesta-una-despedida-de-soltera-por-persona',
    pt: 'quanto-custa-uma-despedida-de-solteira-por-pessoa',
  },
  'workplace-group-gift-amount': {
    en: 'how-much-to-give-for-a-group-gift-at-work',
    fr: 'combien-donner-pour-un-cadeau-collectif-au-bureau',
    es: 'cuanto-poner-para-un-regalo-de-grupo-en-el-trabajo',
    pt: 'quanto-dar-para-uma-prenda-de-grupo-no-trabalho',
  },
  'splitting-versus-budgeting': {
    en: 'splitting-costs-vs-setting-a-budget',
    fr: 'partager-les-frais-ou-fixer-un-budget',
    es: 'dividir-gastos-o-fijar-un-presupuesto',
    pt: 'dividir-despesas-ou-definir-um-orcamento',
  },
};

export function slugFor(id: string, locale: Locale): string | undefined {
  return ARTICLE_SLUGS[id]?.[locale];
}

export function idForSlug(slug: string, locale: Locale): string | undefined {
  for (const [id, slugs] of Object.entries(ARTICLE_SLUGS)) {
    if (slugs[locale] === slug) return id;
  }
  return undefined;
}

/** The languages this article has been translated into, in `LOCALES` order. */
export function localesForArticle(id: string): Locale[] {
  return LOCALES.filter((locale) => slugFor(id, locale) !== undefined);
}
